import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Ban, Check, Globe, History, Loader2, Pencil, Plus, RotateCcw, Search, Trash2, Undo2, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { API } from "@/App";

// Panel admin > Modération > Bans réseau : membres bannis des serveurs du réseau
// (signalés par les bots ou ajoutés ici). Chaque raison est triée en catégorie
// avec sa gravité (#hashtag, mots-clés, puis IA) ; un ban sans raison sert
// seulement à prévenir. Un ban actif part à tous les bots ; chaque serveur qui
// l'a activé applique sa sanction aux catégories qu'il a choisies.

const STATUS = {
  active: { label: "Banni", cls: "bg-red-500/15 text-red-300 border-red-500/30" },
  pending: { label: "En attente", cls: "bg-amber-500/15 text-amber-200 border-amber-500/30" },
  revoked: { label: "Levé", cls: "bg-white/5 text-white/50 border-white/10" },
};
const FILTERS = [["", "Tous"], ["pending", "En attente"], ["active", "Bannis"], ["revoked", "Levés"]];
const SEVERITY = {
  3: "text-red-300 border-red-500/40 bg-red-500/10",
  2: "text-amber-200 border-amber-500/40 bg-amber-500/10",
  1: "text-white/60 border-white/15 bg-white/5",
  0: "text-blue-200 border-blue-500/30 bg-blue-500/10",
};
const SEVERITY_LABEL = { 3: "grave", 2: "moyen", 1: "léger", 0: "alerte seulement" };
const SOURCE_LABEL = { hashtag: "d'après le #hashtag", rules: "d'après les mots de la raison", ai: "trié par l'IA", admin: "choisi à la main", default: "non reconnu", none: "" };

function CategoryBadge({ ban }) {
  return (
    <span className={`text-[11px] px-1.5 py-0.5 border rounded ${SEVERITY[ban.severity] || SEVERITY[1]}`} title={SOURCE_LABEL[ban.category_source] || ""} data-testid={`netban-cat-${ban.user_id}`}>
      {ban.category_label} · {SEVERITY_LABEL[ban.severity] ?? ""}
    </span>
  );
}

const LOG_LABELS = {
  report: "Ban signalé", add: "Ban ajouté", approve: "Ban validé", reject: "Signalement refusé", revoke: "Déban réseau",
  restore: "Ban rétabli", edit: "Raison / catégorie modifiée", delete: "Fiche supprimée", withdrawn: "Signalement retiré", settings: "Réglage",
};
const errorText = (err, fallback) => err?.response?.data?.detail || fallback;
const date = (iso) => (iso ? new Date(iso).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" }) : "");

function BanCard({ ban, act, onEdit }) {
  const [open, setOpen] = useState(false);
  const st = STATUS[ban.status] || STATUS.revoked;
  return (
    <div className="border border-white/10 bg-white/[0.02] rounded-lg p-3" data-testid={`netban-${ban.user_id}`}>
      <div className="flex flex-wrap items-start gap-3">
        {ban.user_avatar ? <img src={ban.user_avatar} alt="" className="w-9 h-9 rounded-full" /> : <div className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center"><Ban className="w-4 h-4 text-white/40" /></div>}
        <div className="flex-1 min-w-[180px]">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-medium text-sm">{ban.user_name || "Membre inconnu"}</span>
            <code className="text-[11px] text-white/40">{ban.user_id}</code>
            <span className={`text-[11px] px-1.5 py-0.5 border rounded ${st.cls}`}>{st.label}</span>
            {ban.category && <CategoryBadge ban={ban} />}
          </div>
          <p className={`text-sm mt-1 break-words ${ban.reason ? "text-white/80" : "text-white/40 italic"}`}>{ban.reason || "Banni sans raison : les serveurs sont seulement prévenus"}</p>
          <p className="text-[11px] text-white/40 mt-1">
            {ban.source === "admin" ? `Ajouté par ${ban.added_by || "un admin"}` : `Banni sur ${ban.source_guild_name || ban.source_guild_id || "un serveur"}`}
            {" · "}{date(ban.created_at)}
            {ban.status === "revoked" && ban.revoked_by ? ` · levé par ${ban.revoked_by}` : ""}
            {(ban.reports || []).length > 1 && <button type="button" className="ml-2 underline hover:text-white" onClick={() => setOpen(!open)}>{ban.reports.length} signalements</button>}
          </p>
        </div>
        <div className="flex flex-wrap gap-1.5">
          {ban.status === "pending" && (
            <>
              <Button size="sm" className="bg-red-600 hover:bg-red-500 h-8" onClick={() => act(ban, "approve")} data-testid={`netban-approve-${ban.user_id}`}><Check className="w-3.5 h-3.5 mr-1" />Bannir du réseau</Button>
              <Button size="sm" variant="outline" className="h-8" onClick={() => act(ban, "reject")}><X className="w-3.5 h-3.5 mr-1" />Refuser</Button>
            </>
          )}
          {ban.status === "active" && <Button size="sm" variant="outline" className="h-8" onClick={() => act(ban, "revoke")} data-testid={`netban-revoke-${ban.user_id}`}><Undo2 className="w-3.5 h-3.5 mr-1" />Débannir</Button>}
          {ban.status === "revoked" && <Button size="sm" variant="outline" className="h-8" onClick={() => act(ban, "restore")}><RotateCcw className="w-3.5 h-3.5 mr-1" />Rebannir</Button>}
          <Button size="sm" variant="ghost" className="h-8 px-2" title="Modifier la raison ou la catégorie" onClick={() => onEdit(ban)}><Pencil className="w-3.5 h-3.5" /></Button>
          <Button size="sm" variant="ghost" className="h-8 px-2 text-red-300" title="Supprimer la fiche" onClick={() => act(ban, "delete")}><Trash2 className="w-3.5 h-3.5" /></Button>
        </div>
      </div>
      {open && (
        <div className="mt-2 border-t border-white/5 pt-2 space-y-1">
          {ban.reports.map((r, i) => (
            <p key={i} className="text-[11px] text-white/50">{date(r.at)} · {r.guild_name || r.guild_id}{r.by ? ` · par ${r.by}` : ""} · {r.reason || "(sans raison)"}</p>
          ))}
        </div>
      )}
    </div>
  );
}

function LogList({ hdr }) {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    axios.get(`${API}/admin/netbans/log`, { headers: hdr() }).then((r) => setRows(r.data.log)).catch(() => setRows([]));
  }, [hdr]);
  if (!rows) return <p className="text-sm text-white/40">Chargement…</p>;
  if (!rows.length) return <p className="text-sm text-white/40">Aucune action pour l'instant.</p>;
  return (
    <div className="divide-y divide-white/5 border border-white/10 rounded-lg" data-testid="netban-log">
      {rows.map((e) => (
        <div key={e.id} className="px-3 py-2 text-xs flex flex-wrap gap-x-2">
          <span className="text-white/40 w-28 shrink-0">{date(e.at)}</span>
          <span className="font-medium">{LOG_LABELS[e.action] || e.action}</span>
          {e.user_name || e.user_id ? <span className="text-white/70">{e.user_name || e.user_id}</span> : null}
          <span className="text-white/40">par {e.by}</span>
          {e.detail && <span className="text-white/60 basis-full sm:basis-auto">— {e.detail}</span>}
        </div>
      ))}
    </div>
  );
}

export default function NetbanPanel({ hdr: parentHdr }) {
  // En-têtes lus via une ref : la fonction du parent change à chacun de ses rendus.
  const hdrRef = useRef(parentHdr);
  hdrRef.current = parentHdr;
  const hdr = useCallback(() => hdrRef.current(), []);
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState("");
  const [category, setCategory] = useState("");
  const [categories, setCategories] = useState([]);
  const [q, setQ] = useState("");
  const [view, setView] = useState("bans");
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => axios.get(`${API}/admin/netbans`, { headers: hdr(), params: { status: filter, q, category } })
    .then((r) => setData(r.data)).catch((err) => toast.error(errorText(err, "Chargement impossible"))), [hdr, filter, q, category]);
  useEffect(() => {
    axios.get(`${API}/admin/netbans/categories`, { headers: hdr() }).then((r) => setCategories(r.data.categories)).catch(() => {});
  }, [hdr]);
  useEffect(() => { const t = setTimeout(load, q ? 300 : 0); return () => clearTimeout(t); }, [load, q]);

  const act = async (ban, action) => {
    const confirmText = {
      revoke: `Débannir ${ban.user_name || ban.user_id} du réseau ? Les serveurs lui rendront ses rôles ou lèveront leur ban.`,
      delete: "Supprimer cette fiche ? Le membre ne sera plus du tout connu du réseau.",
    }[action];
    if (confirmText && !window.confirm(confirmText)) return;
    try {
      if (action === "delete") await axios.delete(`${API}/admin/netbans/${ban.id}`, { headers: hdr() });
      else await axios.post(`${API}/admin/netbans/${ban.id}/${action}`, {}, { headers: hdr() });
      toast.success({ approve: "Banni du réseau : envoyé aux bots", reject: "Signalement refusé", revoke: "Débanni du réseau", restore: "Ban rétabli", delete: "Fiche supprimée" }[action]);
      load();
    } catch (err) { toast.error(errorText(err, "Action impossible")); }
  };

  const setAuto = async (auto) => {
    try {
      await axios.put(`${API}/admin/netbans/settings`, { auto_approve: auto }, { headers: hdr() });
      setData((d) => ({ ...d, auto_approve: auto }));
      toast.success(auto ? "Les bans signalés sont appliqués tout de suite" : "Les bans signalés attendent ta validation");
    } catch (err) { toast.error(errorText(err, "Erreur")); }
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (form.id) await axios.patch(`${API}/admin/netbans/${form.id}`, { reason: form.reason, category: form.category || "auto" }, { headers: hdr() });
      else await axios.post(`${API}/admin/netbans`, { user_id: form.user_id, user_name: form.user_name, reason: form.reason, category: form.category }, { headers: hdr() });
      toast.success(form.id ? "Ban modifié" : "Ajouté aux bans réseau");
      setForm(null);
      load();
    } catch (err) { toast.error(errorText(err, "Enregistrement impossible")); }
    setSaving(false);
  };

  if (!data) return <div className="text-sm text-white/40 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" />Chargement…</div>;
  const c = data.counts;
  return (
    <div className="space-y-5" data-testid="netban-panel">
      <div>
        <h2 className="text-xl font-semibold flex items-center gap-2"><Globe className="w-5 h-5 text-red-400" />Bans réseau</h2>
        <p className="text-sm text-white/50 mt-1">Chaque ban fait sur un serveur d'un bot de gestion arrive ici avec sa raison, triée en catégorie avec sa gravité (#hashtag de la raison, mots-clés, puis IA). Un ban réseau actif est appliqué par les serveurs qui ont activé « Bans réseau » sur leur tableau de bord ({data.enabled_guilds} serveur{data.enabled_guilds > 1 ? "s" : ""} aujourd'hui), selon les catégories qu'ils ont choisies. Sans raison : les serveurs sont seulement prévenus.</p>
      </div>

      <div className="grid grid-cols-3 gap-3">
        {[["pending", "En attente", "text-amber-200"], ["active", "Bannis du réseau", "text-red-300"], ["revoked", "Levés", "text-white/60"]].map(([k, l, cls]) => (
          <button key={k} type="button" onClick={() => { setFilter(k); setView("bans"); }} className={`text-left border rounded-lg p-3 bg-white/[0.02] ${filter === k ? "border-blue-500/50" : "border-white/10"}`}>
            <p className={`text-2xl font-semibold ${cls}`}>{c[k]}</p>
            <p className="text-xs text-white/50">{l}</p>
          </button>
        ))}
      </div>

      <div className="flex items-center justify-between gap-4 border border-white/10 rounded-lg p-3 bg-white/[0.02]">
        <div>
          <p className="text-sm font-medium">Validation automatique</p>
          <p className="text-xs text-white/40 mt-0.5">{data.auto_approve ? "Un ban signalé par un serveur est appliqué au réseau tout de suite." : "Un ban signalé attend que tu le valides (conseillé : un serveur ne peut pas bannir quelqu'un de tout le réseau seul)."}</p>
        </div>
        <button type="button" data-testid="netban-auto" aria-pressed={data.auto_approve} onClick={() => setAuto(!data.auto_approve)}
          className={`w-11 h-6 rounded-full transition-colors relative shrink-0 ${data.auto_approve ? "bg-blue-500" : "bg-white/10"}`}>
          <div className={`w-5 h-5 bg-white rounded-full absolute top-0.5 left-0 transition-transform ${data.auto_approve ? "translate-x-[22px]" : "translate-x-0.5"}`} />
        </button>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <div className="flex border border-white/10 rounded-md overflow-hidden">
          <button type="button" className={`px-3 py-1.5 text-sm ${view === "bans" ? "bg-white/10" : "text-white/50"}`} onClick={() => setView("bans")}>Membres</button>
          <button type="button" className={`px-3 py-1.5 text-sm inline-flex items-center gap-1 ${view === "log" ? "bg-white/10" : "text-white/50"}`} onClick={() => setView("log")} data-testid="netban-log-tab"><History className="w-3.5 h-3.5" />Journal</button>
        </div>
        {view === "bans" && (
          <>
            <div className="flex border border-white/10 rounded-md overflow-hidden">
              {FILTERS.map(([k, l]) => <button key={k} type="button" className={`px-2.5 py-1.5 text-xs ${filter === k ? "bg-blue-600 text-white" : "text-white/50 hover:text-white"}`} onClick={() => setFilter(k)}>{l}</button>)}
            </div>
            <div className="relative flex-1 min-w-[180px]">
              <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-white/30" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Pseudo, ID Discord, raison, serveur…" className="pl-8 h-9" data-testid="netban-search" />
            </div>
            <select value={category} onChange={(e) => setCategory(e.target.value)} className="h-9 rounded-md bg-transparent border border-white/10 px-2 text-xs" data-testid="netban-filter-category" aria-label="Catégorie">
              <option value="" className="bg-[#14151c]">Toutes les catégories</option>
              {categories.map((c) => <option key={c.id} value={c.id} className="bg-[#14151c]">{c.label}</option>)}
              <option value="none" className="bg-[#14151c]">Sans raison</option>
            </select>
            <Button size="sm" className="h-9" onClick={() => setForm({ user_id: "", user_name: "", reason: "", category: "" })} data-testid="netban-add"><Plus className="w-4 h-4 mr-1" />Ajouter un ban</Button>
          </>
        )}
      </div>

      {form && (
        <form onSubmit={submit} className="border border-blue-500/30 bg-blue-500/5 rounded-lg p-4 space-y-3" data-testid="netban-form">
          <p className="text-sm font-medium">{form.id ? `Ban de ${form.user_name || form.user_id}` : "Bannir un membre du réseau"}</p>
          {!form.id && (
            <div className="grid sm:grid-cols-2 gap-3">
              <div><Label className="text-xs">ID Discord</Label><Input value={form.user_id} onChange={(e) => setForm({ ...form, user_id: e.target.value.trim() })} placeholder="123456789012345678" required data-testid="netban-form-id" /></div>
              <div><Label className="text-xs">Pseudo (facultatif)</Label><Input value={form.user_name} onChange={(e) => setForm({ ...form, user_name: e.target.value })} maxLength={80} /></div>
            </div>
          )}
          <div className="grid sm:grid-cols-[1fr_220px] gap-3">
            <div><Label className="text-xs">Raison (envoyée au membre) : vide = les serveurs sont seulement prévenus</Label><Input value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} placeholder="#triche mod menu, arnaque PayPal…" maxLength={500} data-testid="netban-form-reason" /></div>
            <div><Label className="text-xs">Catégorie</Label>
              <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} disabled={!form.reason.trim()} className="w-full h-10 rounded-md bg-transparent border border-white/15 px-2 text-sm disabled:opacity-50" data-testid="netban-form-category">
                <option value="" className="bg-[#14151c]">Automatique (d'après la raison)</option>
                {categories.map((c) => <option key={c.id} value={c.id} className="bg-[#14151c]">{c.label} ({SEVERITY_LABEL[c.severity]})</option>)}
              </select>
            </div>
          </div>
          <div className="flex gap-2">
            <Button type="submit" size="sm" disabled={saving} data-testid="netban-form-save">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4 mr-1" />}{form.id ? "Enregistrer" : "Bannir du réseau"}</Button>
            <Button type="button" size="sm" variant="ghost" onClick={() => setForm(null)}>Annuler</Button>
          </div>
        </form>
      )}

      {view === "log" ? <LogList hdr={hdr} /> : (
        <div className="space-y-2">
          {data.bans.length === 0 && <p className="text-sm text-white/40">Aucun membre{filter ? " dans cette liste" : ""}.</p>}
          {data.bans.map((b) => <BanCard key={b.id} ban={b} act={act} onEdit={(ban) => setForm({ id: ban.id, user_id: ban.user_id, user_name: ban.user_name, reason: ban.reason, category: ban.category_source === "admin" ? ban.category : "" })} />)}
        </div>
      )}
    </div>
  );
}
